/**
 * El mismo proyecto abierto en dos pestañas (paso 1.5). Las pestañas se avisan entre sí
 * (`BroadcastChannel`): al abrir, cada una sabe si el proyecto ya está abierto en otra, y al guardar,
 * las demás se enteran. El guardado es condicionado (`guardarSiNoCambio`): si otra pestaña guardó
 * antes, el autoguardado se detiene y se elige qué cambios conservar. Lo que no se conserva queda
 * como versión en el historial: nada se pierde en silencio.
 */

import { ahoraIso } from '../formato';
import { M } from '../mensajes/es-PE';
import { VERSION_APP } from '../servicios/descargas';
import { repositorio } from '../servicios/repositorio';
import { avisar } from '../ui/avisos';
import { useEditor } from './estado';
import { useSimulacion } from './simulacion';

type Mensaje =
  | { tipo: 'abierto' | 'presente'; proyectoId: string; pestana: string }
  | { tipo: 'guardado'; proyectoId: string; pestana: string; actualizadoEn: string };

const PESTANA =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : String(Date.now()) + String(performance.now());

let canal: BroadcastChannel | null = null;
const elCanal = (): BroadcastChannel | null => {
  if (!canal && typeof BroadcastChannel !== 'undefined')
    canal = new BroadcastChannel('gemba-proyectos');
  return canal;
};

const enviar = (m: Mensaje) => elCanal()?.postMessage(m);

/** Avisa a las demás pestañas que este proyecto se acaba de guardar desde aquí. */
export function anunciarGuardado(proyectoId: string, actualizadoEn: string): void {
  enviar({ tipo: 'guardado', proyectoId, pestana: PESTANA, actualizadoEn });
}

/**
 * Mientras el proyecto está abierto: avisa si también está abierto en otra pestaña y marca el cambio
 * cuando otra pestaña lo guarda. Devuelve la función que deja de escuchar.
 */
export function vigilarOtrasPestanas(proyectoId: string): () => void {
  const c = elCanal();
  if (!c) return () => undefined;
  let avisado = false;
  const avisarUnaVez = () => {
    if (avisado) return;
    avisado = true;
    avisar(M.pestanas.tambienAbierto, 'advertencia', 12000);
  };
  const alMensaje = (e: MessageEvent<Mensaje>) => {
    const m = e.data;
    if (!m || m.proyectoId !== proyectoId || m.pestana === PESTANA) return;
    if (m.tipo === 'abierto') {
      enviar({ tipo: 'presente', proyectoId, pestana: PESTANA });
      avisarUnaVez();
    } else if (m.tipo === 'presente') avisarUnaVez();
    else if (m.tipo === 'guardado') {
      const s = useEditor.getState();
      if (s.proyecto?.id === proyectoId && s.proyecto.actualizadoEn !== m.actualizadoEn)
        s.setCambioEnOtraPestana(true);
    }
  };
  c.addEventListener('message', alMensaje);
  enviar({ tipo: 'abierto', proyectoId, pestana: PESTANA });
  return () => c.removeEventListener('message', alMensaje);
}

const fallo = (e: unknown) =>
  avisar(M.versiones.error(e instanceof Error ? e.message : String(e)), 'error');

/**
 * «Usar el de la otra pestaña»: lo de esta pestaña queda como versión («Antes de cargar los cambios
 * de otra pestaña») y se abre lo guardado. «Deshacer» empieza de cero.
 */
export async function usarLaDeLaOtraPestana(): Promise<void> {
  const mia = useEditor.getState().proyecto;
  if (!mia) return;
  try {
    await repositorio().guardarVersion(mia.id, mia, {
      motivo: 'antes-de:cargar-otra-pestana',
      creadoEn: ahoraIso(),
      versionApp: VERSION_APP,
    });
    const guardada = await repositorio().obtener(mia.id);
    if (!guardada) return;
    useSimulacion.getState().reiniciar();
    useEditor.getState().cargar(guardada);
    avisar(M.pestanas.usada, 'exito');
  } catch (e) {
    fallo(e);
  }
}

/**
 * «Conservar el mío»: lo guardado por la otra pestaña queda como versión («Cambios de otra pestaña
 * que se reemplazaron») y se guarda lo de esta pestaña encima.
 */
export async function conservarLaMia(): Promise<void> {
  const s = useEditor.getState();
  const mia = s.proyecto;
  if (!mia) return;
  try {
    const guardada = await repositorio().obtener(mia.id);
    if (guardada)
      await repositorio().guardarVersion(mia.id, guardada, {
        motivo: 'antes-de:reemplazar-otra-pestana',
        creadoEn: ahoraIso(),
        versionApp: VERSION_APP,
      });
    const iso = ahoraIso();
    const r = await repositorio().guardarSiNoCambio(
      { ...mia, actualizadoEn: iso },
      guardada?.actualizadoEn ?? mia.actualizadoEn,
    );
    if (!r.ok) return; // Otra pestaña volvió a guardar justo ahora: sigue el aviso.
    const ahora = useEditor.getState();
    ahora.marcarGuardado(r.proyecto);
    ahora.setCambioEnOtraPestana(false);
    ahora.setGuardado('guardado');
    anunciarGuardado(mia.id, iso);
    avisar(M.pestanas.conservada, 'exito');
  } catch (e) {
    fallo(e);
  }
}
